import React, { useEffect, useState, useRef, useMemo } from "react";
import QuickReorderCompo from "./QuickReorderCompo";
import ApiPagination from "@/components/apiPagination/ApiPagination";
import { useAppSelector } from "@/store/hooks";
import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchCustomPaginatedQuickReordersQueryOptions } from "@/queryOptions/products/fetchCustomPaginatedQuickReorders";
import { useFilteredQuickReordersSearch } from "@/hooks/useFilteredQuickReordersSearch";
import { useFilteredProductSearch } from "@/hooks/useFilteredProductSearch";
import { LoaderCircle } from "lucide-react";

interface QuickReorderTableWrapperProps {
  handleReorderProduct: any;
  handleStockUpdate: any;
  stockLoading: boolean;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  reorderLoading: boolean;
  className?: string;
}

const QuickReorderTableWrapper: React.FC<QuickReorderTableWrapperProps> = ({
  handleReorderProduct,
  handleStockUpdate,
  stockLoading,
  dialogOpen,
  setDialogOpen,
  reorderLoading,
  className,
}) => {
  const { categories, brands } = useAppSelector((state) => state.app);
  const { user } = useAppSelector((state) => state.auth);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [search, setSearch] = useState("");

  // Infinite query for paginated quick reorders
  const filters = useMemo(
    () => ({
      user_id: { eq: user?.user_id || "" },
    }),
    [user]
  );

  const {
    data: queryResult,
    isLoading: productsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    fetchCustomPaginatedQuickReordersQueryOptions(filters, 25)
  );

  const currentPage = queryResult?.pages?.[currentPageIndex];
  const totalPages = queryResult?.pages?.length || 0;

  // Filtered quick reorder search logic
  const {
    results: searchResults,
    loading: searchLoading,
    error: searchError,
    searchQuickReorders,
    clearResults,
  } = useFilteredQuickReordersSearch({ debounceMs: 300 });

  // Handler for search input from component
  const handleSearchChange = (searchValue: string) => {
    setSearch(searchValue);
    if (searchValue.trim()) {
      setIsSearchActive(true);
      searchQuickReorders({
        search: searchValue.trim(),
        userId: user?.user_id || "",
      });
    } else {
      setIsSearchActive(false);
      clearResults();
    }
  };

  // Orders to display
  const ordersForDisplay = useMemo(() => {
    if (isSearchActive) {
      return searchResults;
    }
    return currentPage?.items || [];
  }, [isSearchActive, searchResults, currentPage]);

  // Pagination handlers
  const handlePrevious = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };

  const handleNext = async () => {
    const nextPageIndex = currentPageIndex + 1;
    if (queryResult?.pages?.[nextPageIndex]) {
      setCurrentPageIndex(nextPageIndex);
      if (!queryResult.pages[nextPageIndex + 1] && hasNextPage) {
        fetchNextPage();
      }
    } else if (hasNextPage) {
      await fetchNextPage();
      setCurrentPageIndex(nextPageIndex);
    }
  };

  // Loading and error states
  const isLoading = productsLoading || searchLoading;
  const showNoResults =
    !isLoading && ordersForDisplay.length === 0 && isSearchActive;

    if ( isLoading) {
      return (
        <div className="flex items-center justify-center w-full h-full">
          <LoaderCircle className="w-6 h-6 animate-spin mr-4 "></LoaderCircle>
          Fetching Quick Reorder Orders. This won’t take long!
        </div>
      );
    }

  return (
    <div>
      <QuickReorderCompo
        handleReorderProduct={handleReorderProduct}
        products={ordersForDisplay as any}
        categories={categories}
        handleStockUpdate={handleStockUpdate}
        stockLoading={stockLoading}
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        reorderLoading={reorderLoading}
        className={className}
        onSearchChange={handleSearchChange}
        children={
          <ApiPagination
          currentPageIndex={currentPageIndex}
          totalPages={totalPages}
          hasNextPage={
            currentPageIndex < (queryResult?.pages?.length || 0) - 1 ||
            hasNextPage
          }
          isFetching={productsLoading || isFetchingNextPage}
          onPrevious={handlePrevious}
          onNext={handleNext}
          variant="minimal"
        />
        }
      />

    
    
        
       
   
      {searchError && (
        <div className="text-red-500 text-center py-4">
          Error: {searchError}
        </div>
      )}
      {showNoResults && (
        <div className="flex flex-col justify-center items-center h-64 text-gray-500">
          <span className="text-lg font-medium mb-2">
            No quick reorder orders found
          </span>
          <span className="text-sm">
            Try adjusting your search terms to find more orders.
          </span>
        </div>
      )}
    </div>
  );
};

export default QuickReorderTableWrapper;
